import React, { useCallback } from "react";

import ReactMarkDown from "react-markdown";
import remarkGfm from "remark-gfm";

import emoji from "emoji-dictionary";
import _ from "lodash";

import "./_css/github-markdown-light.css";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import * as theme from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import { VStack, XButton, XDiv, XImage } from "IZOX/_X";
import { GFunc } from "IZOX/STATIC";

/**
 * @typedef {import("IZOX/_X/XDiv").xdivparams} xdivparams
 * @typedef {import("IZOX/_X/VStack").vstackparams} vstackparams
 * 
 * @typedef {Object} markdownparamsAddOns
 * @property {string|number} [width="100%"]
 * @property {string|number} [height="fit-content"]
 * @property {string} [codeTheme="vs"] - Code theme for syntax highlighting. Options include "vs", "vs2015", "atomDark", "base16AteliersulphurpoolLight", "base16AteliersulphurpoolDark", "solarizedlight", "solarizeddark", "tomorrow", "tomorrowNight", "twilight", "coy", "dark", "funky", "okaidia", "pojoaque", "prism", "shadesOfPurple".
 * @property {string} [className=""] - Additional class names for the markdown container.
 * @property {import("react").CSSProperties} [highlighterStyle] - Custom style object for syntax highlighter.
 * @property {vstackparams} [containerParams] - Custom style object for the markdown container.
 * @property {boolean} [skipHtml=false] - Whether to skip HTML parsing in markdown.
 * @property {"[" | "]" | "|" } [textAlign="|"] - Text alignment option for the markdown content.
 * 
 * @typedef {xdivparams & markdownparamsAddOns} markdownparams
 * 
 * @param {markdownparams} param0 
 * @returns 
 */
function Markdown({
  width = "100%",
  height = "fit-content",
  codeTheme = "vs",
  className = "",
  highlighterStyle,
  containerParams,
  children,
  skipHtml = false,
  textAlign = "|",
  ...others
}){

  const emojiSupport = useCallback((text) => {
    return text.replace(/:(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]):/gi, name => emoji.getUnicode(name));
  }, []);

  if(_.isArray(children)){
    children = children.join("\n");
  }

  if(!_.isString(children)) return <XDiv/>;
  let emojied = emojiSupport(children);

  return (
    <XDiv width={width} height={height} {...others}>
      <VStack className={className + " markdown-body"} HAlign={textAlign} {...containerParams}>
        <ReactMarkDown 
          disallowedElements={["script", "style"]}
          unwrapDisallowed={true}
          linkTarget="_blank"
          children={emojied} 
          skipHtml={skipHtml} 
          remarkPlugins={[[remarkGfm]]}
          rehypePlugins={[rehypeRaw]}
          components={{
            code({node, inline, className, children, ...props}) {
              const match = /language-(\w+)/.exec(className || "");
              return !inline && match ? (
                <SyntaxHighlighter
                  children={String(children).replace(/\n$/, "")}
                  style={highlighterStyle ?? theme[codeTheme]}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                />
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            },
            img({node, ...props}){
              const {src, alt} = props;
              if(src){
                return (
                  <XButton onPress={() => GFunc.ViewImage(src)} fitWidthToContent>
                    <XImage source={src} alt={alt} fitWidthToContent/>
                  </XButton>
                );
              }
              return null;
            }
          }}
          />
      </VStack>
    </XDiv>
  );
}

export default Markdown;
